/**
 * A13b - resolve public location label from customerPresence.
 * ONLINE_ONLY null location -> Shipping only (never locationMissing).
 * STOREFRONT null -> caller-supplied locationMissing key.
 */

import {
  CUSTOMER_PRESENCE_SHIPPING_ONLY_I18N_KEY,
  isOnlineOnlyCustomerPresence,
  parseSalesPointCustomerPresence,
  type SalesPointCustomerPresence,
} from './customerPresence.js';

export type ResolveShopLocationLabelInput = {
  readonly customerPresence?: SalesPointCustomerPresence | null | unknown;
  readonly location: string | null | undefined;
  /** Already-translated STOREFRONT missing-location fallback. */
  readonly locationMissingLabel: string;
  /** Already-translated Shipping-only label (`customerPresence.shippingOnly`). */
  readonly shippingOnlyLabel: string;
};

/**
 * Public directory / contacts / shop meta location line.
 * ONLINE_ONLY always returns shippingOnlyLabel (ops address must not leak).
 */
export function resolveShopLocationLabel(
  input: ResolveShopLocationLabelInput,
): string {
  const presence = parseSalesPointCustomerPresence(input.customerPresence);
  if (isOnlineOnlyCustomerPresence(presence)) {
    return input.shippingOnlyLabel;
  }
  const trimmed =
    typeof input.location === 'string' ? input.location.trim() : '';
  if (trimmed.length > 0) {
    return trimmed;
  }
  return input.locationMissingLabel;
}

/** Convenience when the caller has `t` from react-i18next. */
export function resolveShopLocationLabelWithT(
  input: {
    readonly customerPresence?: SalesPointCustomerPresence | null | unknown;
    readonly location: string | null | undefined;
    readonly locationMissingKey?: string;
  },
  t: (key: string) => string,
): string {
  return resolveShopLocationLabel({
    customerPresence: input.customerPresence,
    location: input.location,
    locationMissingLabel: t(
      input.locationMissingKey ?? 'retail.entry.shops.locationMissing',
    ),
    shippingOnlyLabel: t(CUSTOMER_PRESENCE_SHIPPING_ONLY_I18N_KEY),
  });
}